import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamation } from "@fortawesome/free-solid-svg-icons";
import "../../listing-calendar-main.scss";

export default function CalendarCell({
  day,
  currentMonthIndex,
  dayIndex,
  dayReservations = [],
  onClick,
}) {
  const dayKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
  const isCurrentMonth = day.getMonth() === currentMonthIndex;
  const hasAnyReservation = dayReservations.length > 0;
  const isWeekend = dayIndex === 5 || dayIndex === 6;

  // helper to normalize status from different shapes
  const getStatus = (r) => {
    const s = (r.listingDateStatus || r.status || r.raw?.status || "").toString().toLowerCase();
    return s;
  };

  const hasApproved =
    hasAnyReservation &&
    dayReservations.some((r) => getStatus(r) === "approved");

  // true when every item for the date is blocked
  const allBlocked =
    hasAnyReservation &&
    dayReservations.every((r) => getStatus(r) === "blocked");

  // whether there's at least one blocked item (mixed case)
  const hasBlocked =
    hasAnyReservation &&
    dayReservations.some((r) => getStatus(r) === "blocked");

  // whether any blocked item is marked as holiday (support both isHoliday and legacy holiday)
  const hasHoliday =
    hasAnyReservation &&
    dayReservations.some((r) => {
      return Boolean(
        r.isHoliday === true ||
        r.raw?.isHoliday === true ||
        r.raw?.holiday === true ||
        r.is_holiday === true
      );
    });

  // a non-holiday blocked date: there is at least one blocked status and NO holiday flag
  const isNonHolidayBlocked = hasBlocked && !hasHoliday;

  // clickable only when NOT weekend and there are reservations (empty cells are not clickable)
  // but force non-clickable for non-holiday blocked dates
  let isClickable = !isWeekend && hasAnyReservation;
  if (isNonHolidayBlocked) {
    isClickable = false;
  }

  const cellClassNames = [
    "calendar-cell",
    !isCurrentMonth && "calendar-cell--offmonth",
    hasAnyReservation && "calendar-cell--has-reservation",
    hasApproved && "calendar-cell--approved",
    hasBlocked && "calendar-cell--blocked",
    hasHoliday && "calendar-cell--holiday",
    // disabled for weekends OR when it's a non-holiday blocked date
    (isWeekend || isNonHolidayBlocked) && "calendar-cell--disabled",
    isClickable && "calendar-cell--clickable",
  ]
    .filter(Boolean)
    .join(" ");

  // Build the institutions list but only show the first/only one
  // IMPORTANT: don't show notes/reason when the date is a non-holiday blocked date.
  const institutions = dayReservations
    .map((r) => {
      if (getStatus(r) === "blocked") {
        // if this date is non-holiday blocked, return null so nothing renders
        if (isNonHolidayBlocked) {
          return null;
        }
        return r.notes || r.reason || r.raw?.notes || r.raw?.reason || null;
      }
      // non-blocked reservations: show institution if available
      return r.institution || r.raw?.institution || null;
    })
    .filter(Boolean);

  const firstInstitution = institutions.length > 0 ? institutions[0] : null;

  // show the exclamation only when there are reservations (not when all items are blocked)
  // hide warnings for non-holiday blocked dates as requested
  const showWarningBadge = hasAnyReservation && !hasApproved && !isWeekend && !allBlocked && !isNonHolidayBlocked;

  const handleKeyDown = (e) => {
    if (!isClickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick && onClick(day);
    }
  };

  return (
    <td
      key={dayKey}
      className={cellClassNames}
      onClick={() => {
        if (!isClickable) return;
        onClick && onClick(day);
      }}
      onKeyDown={handleKeyDown}
      role={isClickable ? "button" : undefined}
      aria-disabled={!isClickable}
      tabIndex={isClickable ? 0 : -1}
    >
      <div className="cell-top-row">
        <div className="cell-day-number">{day.getDate()}</div>

        {showWarningBadge && (
          <div className="cell-badge-count" title="Pending reservation(s)">
            <FontAwesomeIcon icon={faExclamation} />
          </div>
        )}
      </div>

      {/* only render institution block when we actually have content */}
      {institutions.length > 0 && (
        <div className="cell-institutions">
          <div
            className="cell-institution-row"
            title={firstInstitution}
            aria-label={firstInstitution ? `Reservation by ${firstInstitution}` : undefined}
          >
            {firstInstitution}
          </div>
        </div>
      )}
    </td>
  );
}